import React, { useState } from 'react'
import moment from 'moment'
import { Popover, PopoverBody } from 'reactstrap'
import { BsThreeDots } from 'react-icons/bs'
import { BiFolder } from 'react-icons/bi'
import ReactTooltip from 'react-tooltip'
import {
  LayoutRowShadow,
  LayoutData,
} from '../../../components/TableComponents/RowCardShadow/RowCardShadow'
import { formatItemCount } from './folderService'

// A folder rendered as a table row that visually matches the trivvy rows.
// - Native HTML5 drop target (a trivvy row can be dragged onto it).
// - Not draggable itself.
// - Item count lives in the Name column (never in Status).
// - The folder's date renders in the middle cell at `createdColIndex` (aligned
//   under the Created column); every other middle cell renders blank so the row
//   still lines up with the trivvy rows.
const FolderRow = ({
  folder,
  itemCount,
  itemNoun,
  nameColWidth,
  middleColWidths,
  createdColIndex,
  actionColWidth,
  isAdmin,
  isDragOver,
  onOpen,
  onDragOver,
  onDragLeave,
  onDrop,
  onRename,
  onDelete,
}) => {
  const [menuOpen, setMenuOpen] = useState(false)

  const popoverId = `folderListRow_${folder._id}`
  const blanks = middleColWidths || []

  // Matches the trivvy Created cell (survey-createddate-text, two lines). Folders
  // show their last-updated date labelled "Updated" (per the Figma) — a rename
  // bumps it. The Created column sorts folders by this same updatedAt field, so
  // the visible dates always track the sort order.
  const createdContent =
    folder && folder.updatedAt ? (
      <div className="survey-createddate-text">
        <div>Updated</div>
        <div>{moment(folder.updatedAt).format('MMM D, YYYY')}</div>
      </div>
    ) : null

  const handleDragOver = (e) => {
    // Must preventDefault so the row is a valid drop target.
    e.preventDefault()
    if (onDragOver) {
      onDragOver(e)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    if (onDrop) {
      onDrop(e)
    }
  }

  return (
    <LayoutRowShadow
      className={`folder-row ${isDragOver ? 'folder-row-dragover' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={onDragLeave}
      onDrop={handleDrop}
    >
      <LayoutData colWidth={nameColWidth} className="cursor-pointer">
        <div
          className="d-flex trivvy-tbl-list-row-name-col folder-row-name-col"
          onClick={() => {
            if (onOpen) {
              onOpen()
            }
          }}
          data-tip={folder.name}
        >
          <div className="folder-row-icon-wrap">
            <BiFolder className="folder-row-icon" />
          </div>
          <span className="survey-item-title d-block">
            {folder.name}
            <div className="folder-row-count">
              {formatItemCount(itemCount, itemNoun)}
            </div>
            <ReactTooltip />
          </span>
        </div>
      </LayoutData>

      {blanks.map((width, idx) => (
        <LayoutData
          key={idx}
          colWidth={width}
          className="text-center"
        >
          {idx === createdColIndex ? createdContent : null}
        </LayoutData>
      ))}

      <LayoutData colWidth={actionColWidth} className="text-center">
        {isAdmin ? (
          <div className="cursor-pointer">
            <span
              id={popoverId}
              className="cursor-pointer"
              onClick={() => setMenuOpen(true)}
            >
              <BsThreeDots />
            </span>
            <Popover
              isOpen={menuOpen}
              target={popoverId}
              toggle={() => setMenuOpen(false)}
              placement="bottom"
              modifiers={{
                flip: { boundariesElement: 'viewport' },
                preventOverflow: { boundariesElement: 'viewport' },
              }}
              hideArrow
              className="surveyListRow_Popover"
            >
              <PopoverBody>
                <div
                  className="dropdown-item my-1 cursor-pointer"
                  onClick={() => {
                    setMenuOpen(false)
                    if (onOpen) {
                      onOpen()
                    }
                  }}
                >
                  Open folder
                </div>
                <div
                  className="dropdown-item my-1 cursor-pointer"
                  onClick={() => {
                    setMenuOpen(false)
                    if (onRename) {
                      onRename(folder)
                    }
                  }}
                >
                  Rename folder
                </div>
                <div
                  className="dropdown-item my-1 cursor-pointer folder-menu-item-delete"
                  onClick={() => {
                    setMenuOpen(false)
                    if (onDelete) {
                      onDelete(folder)
                    }
                  }}
                >
                  Delete folder
                </div>
              </PopoverBody>
            </Popover>
          </div>
        ) : null}
      </LayoutData>
    </LayoutRowShadow>
  )
}

export default FolderRow
